import {useEffect, useMemo, useRef, useState} from 'react';
import DefaultDoctorIcon from '@/assets/image/circule_doctor_any.png';
import NoPhotoDoctorIcon from '@/assets/image/circule_doctor_no_img.png';
import OlgaImg from '@/assets/image/olga_dok.png';
import MarfaImg from '@/assets/image/marfa_dok.png';
import MajImg from '@/assets/image/maj_dok.png';
import MarchinImg from '@/assets/image/dok_marchin.png';
import DmitryImg from '@/assets/image/dmitry_dok.png';
import LeszekImg from '@/assets/image/leszek_rymsza_smal.png';

import IgaImg from '@/assets/image/iga_kozłowska_cir.png';
import MarharytaImg from '@/assets/image/marharyta_sementsova_cir.png';

import { cn } from "@/lib/utils"

import styles from './DoctorsSelectMobile.module.scss';

type Doctor = {
  id: string;
  name: string;
  office_id: string[] | number[];
};

interface DoctorsSelectMobileProps {
  value: {id: string; name: string};
  onChange: (val: {id: string; name: string}) => void;
  officeId?: string; // comma separated office ids string
  doctors: Doctor[];
  availableDoctorIds?: Set<string>;
}

const ARROW_SIZE = 24;

const DoctorsSelectMobile = ({
  value,
  onChange,
  officeId,
  doctors,
  availableDoctorIds
}: DoctorsSelectMobileProps) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const filteredDoctors: Doctor[] = useMemo(() => {
    if (!officeId || doctors.length === 0) return doctors || [];
    const officeIds = officeId
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    let result = doctors.filter((doctor) =>
      doctor.office_id.some((o) => officeIds.includes(String(o)))
    );
    if (availableDoctorIds && availableDoctorIds.size > 0) {
      result = result.filter((d) => availableDoctorIds.has(String(d.id)));
    }
    return result;
  }, [officeId, doctors, availableDoctorIds]);

  const getDoctorImage = (doctor?: {id?: string; name?: string}) => {
    const idStr = String(doctor?.id || '');
    switch (idStr) {
      case '21':
        return OlgaImg;
      case '33':
        return MarfaImg;
      case '31':
        return MajImg;
      case '35':
        return IgaImg;
      case '34':
        return MarharytaImg;
      case '43':
        return MarchinImg;
      case '18':
        return DmitryImg;
      case '45':
        return LeszekImg;
      default:
        return NoPhotoDoctorIcon;
    }
  };

  const updateArrows = () => {
    const el = listRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 8);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = listRef.current;
    if (!el) return;
    const handler = () => updateArrows();
    el.addEventListener('scroll', handler, {passive: true});
    window.addEventListener('resize', handler);
    return () => {
      el.removeEventListener('scroll', handler as any);
      window.removeEventListener('resize', handler);
    };
  }, [filteredDoctors.length]);

  const scroll = (dir: 'left' | 'right') => {
    const el = listRef.current;
    if (!el) return;
    const delta = Math.round(el.clientWidth * 0.85);
    el.scrollBy({left: dir === 'left' ? -delta : delta, behavior: 'smooth'});
  };

  const selectDoctor = (doctorId: string) => {
    if (doctorId === '999') {
      onChange({id: '', name: 'Dowolny lekarz'});
      return;
    }
    const selected = filteredDoctors.find((d) => String(d.id) === doctorId);
    if (selected) onChange({id: String(selected.id), name: selected.name});
  };

  const isSelected = (id: string) => String(value?.id || '') === String(id);

  return (
    <div className='r-form-field-item-container'>
      <h3 className='mb-2 text-[16px] font-semibold text-left sm:text-left md:text-lg'>
        Wybierz lekarza
      </h3>
      <div className='relative'>
        {showLeft && (
          <button
            type='button'
            aria-label='prev'
            onClick={() => scroll('left')}
            className='absolute left-0 top-0  ml-[-21px] z-10 h-12 w-10 flex items-center justify-center bg-[#ADC631] '
          >
            <svg width={ARROW_SIZE} height={ARROW_SIZE} viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M15 6L9 12L15 18' stroke='#FFFFFF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            </svg>
          </button>
        )}

        {showRight && (
          <button
            type='button'
            aria-label='next'
            onClick={() => scroll('right')}
            className='absolute right-0 top-0  mr-[-21px] z-10 h-12 w-10 flex items-center justify-center bg-[#ADC631] '
          >
            <svg width={ARROW_SIZE} height={ARROW_SIZE} viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M9 6L15 12L9 18' stroke='#FFFFFF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            </svg>
          </button>
        )}

        <div
          ref={listRef}
          className={cn('flex gap-2 overflow-x-auto px-2 scroll-smooth', `${styles['scrollbar-custom']}`)}
          style={{scrollSnapType: 'x mandatory'}}
        >
          {/* Any doctor */}
          <button
            type='button'
            onClick={() => selectDoctor('999')}
            className=' flex flex-col items-center min-w-[64px] mr-4'
            style={{scrollSnapAlign: 'start'}}
          >
            <div
              className={
                'relative h-12 w-12 rounded-full flex items-center justify-center bg-[#e8f0cd] border-2 ' +
                (value?.id ? 'border-transparent' : 'border-[#9bb52a]')
              }
            >
              <img
                src={DefaultDoctorIcon}
                alt='Dowolny lekarz'
                className='h-10 w-10 rounded-full object-cover'
              />
               <div className='absolute top-0 right-0 mr-[-14px] w-[1px] h-full bg-[#E0E0E0]'></div>
          
            </div>
            <span className={'mt-2 text-[12px] text-center ' + (!value?.id ? 'text-[#7fa120] font-semibold' : 'text-[#1b1b1b]')}><span>Dowolny lekarz</span></span>
           </button>
         

          {filteredDoctors.map((doctor) => (
            <button
              key={String(doctor.id)}
              type='button'
              onClick={() => selectDoctor(String(doctor.id))}
              className='flex flex-col items-center min-w-[84px]'
              style={{scrollSnapAlign: 'start'}}
            >
              <div
                className={
                  'h-12 w-12 rounded-full  bg-[#e8f0cd] border-2 ' +
                  (isSelected(String(doctor.id))
                    ? 'border-[#9bb52a]'
                    : 'border-transparent')
                }
              >
                <img
                  src={getDoctorImage({id: String(doctor.id), name: doctor.name})}
                  alt={doctor.name}
                  style={{height: '100%'}}
                  className=' w-full rounded-full object-cover'
                />
              </div>
              <span
                className={
                  'mt-2 text-[12px] text-center text-[#1b1b1b] ' +
                  (isSelected(String(doctor.id)) ? 'text-[#7fa120] font-semibold' : '')
                }
              >
                <span>{doctor.name}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DoctorsSelectMobile;